import React, { useState } from 'react';
import DropdownCustom from '../../../moduleComponents/DropdownCustom';
import { getL0Options, getL1Options, getL2Options, buildL0Key } from '../../../moduleServices/helper_hierarchicalCategory';
import { getOrCreateBudgetPlan, createBudgetPlanItem } from '../../viewServices/budget_api';
import { useFinanceProfile } from '../../../moduleServices/finance_profilecontext';
import SpinnerLoad from '../../../moduleComponents/SpinnerLoad';

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function getYearOptions() {
  const y = new Date().getFullYear();
  return [String(y - 2), String(y - 1), String(y), String(y + 1), String(y + 2)];
}

function buildDate(year, month) {
  return `${year}-${month}-01`;
}

const FormBudgetAdd = ({ hierarchicalCategories, onCreated, onCancel }) => {
  const { profile } = useFinanceProfile();
  const defaultCurrency = profile?.default_currency || 'BRL';

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [budgetType, setBudgetType] = useState('Out');
  const [classL0, setClassL0] = useState('');
  const [classL1, setClassL1] = useState('');
  const [classL2, setClassL2] = useState('');
  const [formMsg, setFormMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleBudgetType = (type) => {
    setBudgetType(type);
    // Reset L0 since keys include type
    setClassL0('');
    setClassL1('');
    setClassL2('');
  };

  const handleL0 = (option) => {
    setClassL0(typeof option === 'object' ? option.value : option);
    setClassL1('');
    setClassL2('');
    setFormMsg(null);
  };

  const handleL1 = (option) => {
    setClassL1(typeof option === 'object' ? option.value : option);
    setClassL2('');
    setFormMsg(null);
  };

  const handleL2 = (option) => {
    setClassL2(typeof option === 'object' ? option.value : option);
    setFormMsg(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormMsg(null);
    if (!classL0) {
      setFormMsg('Please select a category group.');
      return;
    }
    const date = buildDate(selectedYear, selectedMonth);
    setLoading(true);
    try {
      const l0Name = hierarchicalCategories.by_l0?.[classL0]?.name || '';
      const l1Name = hierarchicalCategories.by_l1?.[classL1]?.name || '';
      const l2Name = hierarchicalCategories.by_l2?.[classL2]?.name || '';

      const planRes = await getOrCreateBudgetPlan(date);
      const planId = planRes?.data?.id;
      if (!planId) throw new Error('Could not resolve budget plan.');

      const payload = {
        plan: planId,
        class_l0: l0Name,
        class_l1: l1Name,
        class_l2: l2Name,
        budget_type: budgetType,
        budget_amount: 0,
        description: '-',
        budget_currency: defaultCurrency,
      };
      const res = await createBudgetPlanItem(payload);
      if (onCreated) onCreated(res?.data);
    } catch {
      setFormMsg('Failed to create budget. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const l1Options = getL1Options(hierarchicalCategories, classL0);
  const l2Options = getL2Options(hierarchicalCategories, classL1);

  return (
    <form onSubmit={handleSubmit} className="relative">
      {loading && (
        <div className="absolute inset-0 bg-white/70 rounded-xl flex items-center justify-center z-10">
          <SpinnerLoad />
        </div>
      )}

      {/* Month / Year */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Month</label>
        <div className="flex gap-2">
          <select
            value={selectedMonth}
            onChange={(e) => { setSelectedMonth(e.target.value); setFormMsg(null); }}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-700"
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => { setSelectedYear(e.target.value); setFormMsg(null); }}
            className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-700"
          >
            {getYearOptions().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* In / Out */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Type</label>
        <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
          <button
            type="button"
            onClick={() => handleBudgetType('In')}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              budgetType === 'In' ? 'bg-green-700 text-white shadow' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            In
          </button>
          <button
            type="button"
            onClick={() => handleBudgetType('Out')}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              budgetType === 'Out' ? 'bg-red-600 text-white shadow' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Out
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Category</label>
        <div className="space-y-2">
          <DropdownCustom
            options={getL0Options(hierarchicalCategories, budgetType)}
            selectedValue={classL0}
            onSelect={handleL0}
            placeholder="Group"
          />
          <DropdownCustom
            options={l1Options}
            selectedValue={classL1}
            onSelect={handleL1}
            placeholder="Category"
          />
          <DropdownCustom
            options={l2Options}
            selectedValue={classL2}
            onSelect={handleL2}
            placeholder="Sub-Category"
          />
        </div>
      </div>

      {formMsg && (
        <div className="mb-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {formMsg}
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-green-800 hover:bg-green-900 text-white transition-colors disabled:opacity-50"
        >
          Create
        </button>
      </div>
    </form>
  );
};

export default FormBudgetAdd;
